import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import dayjs from "dayjs";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";

import { showSnackbar } from "../../../api/snackbarSlice";
import TimePeriodField, {
  inclusiveDayCount,
} from "../../../components/form/TimePeriodField";
import { auditClient } from "../../../utils/axios";

const MAX_RANGE_DAYS = 90;

const exportErrorMessage = async (error) => {
  if (error?.response?.status === 403) {
    return "You do not have permission to generate audit log reports.";
  }

  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return (
        parsed?.errors?.message ||
        parsed?.message ||
        "Failed to generate the audit log report."
      );
    } catch {
      return "Failed to generate the audit log report.";
    }
  }

  return (
    data?.message ||
    data?.errors?.message ||
    "Failed to generate the audit log report."
  );
};

const fileNameFor = (startDate, endDate) =>
  `logs_${startDate}_to_${endDate}.xlsx`;

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const exportRange = async (startDate, endDate) => {
  const { data } = await auditClient.get("/logs/export", {
    params: {
      start_time: `${startDate}T00:00:00.000Z`,
      end_time: `${endDate}T23:59:59.000Z`,
    },
    responseType: "blob",
  });

  downloadBlob(data, fileNameFor(startDate, endDate));
};

const AuditLogSection = () => {
  const dispatch = useDispatch();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      startDate: dayjs().subtract(6, "day"),
      endDate: dayjs(),
    },
  });
  const startDate = useWatch({ control, name: "startDate" });
  const endDate = useWatch({ control, name: "endDate" });
  const dayCount = inclusiveDayCount(startDate, endDate);
  const rangeInvalid = dayCount === null || dayCount > MAX_RANGE_DAYS;

  const handleDateRangeChange = (start, end) => {
    setValue("startDate", start, { shouldDirty: true });
    setValue("endDate", end, { shouldDirty: true });
  };

  const handleExportRange = async (start, end) => {
    const startValue = dayjs(start).format("YYYY-MM-DD");
    const endValue = dayjs(end).format("YYYY-MM-DD");
    setCreating(true);
    try {
      await exportRange(startValue, endValue);
      setDialogOpen(false);
      dispatch(
        showSnackbar({
          message: "Audit log report downloaded successfully.",
          severity: "success",
        }),
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: await exportErrorMessage(error),
          severity: "error",
        }),
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={2}
        mb={3}
      >
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Audit Logs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track all actions and security events
          </Typography>
        </Box>
        <Button
          id="export-audit-logs"
          variant="contained"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={() => {
            window.setTimeout(() => setDialogOpen(true), 0);
          }}
          sx={{ whiteSpace: "nowrap" }}
        >
          Export Audit Log
        </Button>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => !creating && setDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        disableEnforceFocus
      >
        <DialogTitle>Export Audit Log</DialogTitle>
        <DialogContent>
          <TimePeriodField
            key={dialogOpen ? "open" : "closed"}
            startDate={startDate}
            endDate={endDate}
            maxDays={MAX_RANGE_DAYS}
            required
            sx={{ mt: 1 }}
            onChange={handleDateRangeChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={rangeInvalid || creating}
            onClick={handleSubmit(({ startDate: start, endDate: end }) =>
              handleExportRange(start, end),
            )}
          >
            {creating ? "Downloading..." : "Generate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogSection;
